import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  getTachographCompanySettingsByCustomerId,
  saveTachographCompanySettings,
  type TachographCompanySettingsPayload,
} from "src/services/tachographCompanyService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

export const useQueryGetTachographCompanySettings = (
  customerId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = Boolean(customerId) && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.tachographCompanySettings, customerId, language],
      queryFn: () =>
        getTachographCompanySettingsByCustomerId(
          customerId as string | number,
          language,
        ),
      enabled,
      retry: false,
      ...options,
    }),
    enabled,
  );
};

export const useMutationSaveTachographCompanySettings = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: TachographCompanySettingsPayload) =>
      saveTachographCompanySettings(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tachographCompanySettings,
      }),
  });
};
